import { animated, useSpring } from '@react-spring/web';
import { useNetworkConnectionStatus } from '@talos/kyoko/src/providers/NetworkConnectionStatusProvider';
import { useState } from 'react';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { Box } from '../Core';
import { Icon, IconName } from '../Icons';
import { Text } from '../Text';

export function NetworkConnectionBar() {
  const { text } = useNetworkConnectionStatus();
  const [displayed, setDisplayed] = useState(false);
  const { fontSizeTitleH3 } = useTheme();

  const [{ height }] = useSpring(
    {
      height: `${text ? `28px` : '0px'}`,
      config: { duration: 300, mass: 5, friction: 120, tension: 120 },
      onRest: () => setDisplayed(!!text),
    },
    [text, fontSizeTitleH3]
  );

  return (
    <Container data-testid="network-connection-bar" style={{ height }} data-displayed={displayed} displayed={displayed}>
      <BackgroundColorWrapper />
      {displayed && text && (
        <TextWrapper>
          <>
            <Icon icon={IconName.StatusOffline} />
            <Text>{text}</Text>
          </>
        </TextWrapper>
      )}
    </Container>
  );
}

const Container = styled(animated.div)<{ displayed?: boolean }>`
  align-items: center;
  position: relative;
  justify-content: center;
  display: flex;
  font-size: ${({ theme }) => theme.fontSizeSm}rem;

  ${({ theme, displayed }) => css`
    gap: ${theme.spacing4}px;
    color: ${theme.colorTextImportant};
    background: ${theme.colors.gray['020']};
    ${displayed &&
    css`
      border-bottom: 1px solid ${theme.colors.gray['000']};
    `}
  `}
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const TextWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 0;
  animation: ${fadeIn} 200ms linear forwards;
  animation-delay: 100ms;
`;

// changes opacity with keyframes
const pulse = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0.24;
  }
  100% {
    opacity: 0;
  }
`;

// More performant to change opacity than to transition color. So add a content wrapper which changes opacity.
const BackgroundColorWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  ${({ theme }) => css`
    background: ${theme.colors.blue.default};
  `}
  animation: ${pulse} 5s infinite;
`;
